import type {
  Locale,
  RoomTranslationMode,
  RoomTranslationSettings,
  TranslationGlobalSettings,
  TranslationMode,
} from './types';

// ── 번역 모드 상속 (PLAN 5-7-1장) ────────────────────────────────────

/** 번역문이 노출되는 위치 */
export type TranslateSlot = 'list' | 'notification' | 'body';

/**
 * 방별 모드가 'inherit'면 전역 설정을 상속한다.
 * 순수 함수로 격리해 4×5 조합 테스트 가능.
 */
export function resolveMode(global: TranslationMode, room: RoomTranslationMode): TranslationMode {
  return room === 'inherit' ? global : room;
}

/** 전역 설정 × 방별 설정을 합친 실제 적용값 (PLAN 5-7-1장) */
export interface EffectiveTranslation {
  mode: TranslationMode;
  /** 번역 대상 언어 — 'auto'면 자동 감지 결과를 쓴다 */
  targetLang: Locale | 'auto';
  /** 이 방이 전역과 다른 설정을 쓰는가 (헤더/목록 뱃지 표시용) */
  overridden: boolean;
}

/**
 * 상속 규칙을 한 곳에 모은다 — 헤더 뱃지, 본문 번역, 목록/알림 판정이
 * 모두 이 함수를 거치므로 화면마다 판정이 어긋나지 않는다.
 */
export function effectiveTranslation(
  global: TranslationGlobalSettings,
  room?: RoomTranslationSettings,
): EffectiveTranslation {
  const roomMode = room?.mode ?? 'inherit';
  return {
    mode: resolveMode(global.globalMode, roomMode),
    targetLang: resolveTargetLang(
      global.targetLang,
      global.autoDetectTarget,
      room?.targetLangOverride,
    ),
    overridden: roomMode !== 'inherit' || room?.targetLangOverride !== undefined,
  };
}

/** 모드별 표시 위치 판정 (5-7-1장 동작 표) */
export function shouldTranslate(
  effective: TranslationMode,
  slot: TranslateSlot,
  isOwnMessage: boolean,
): boolean {
  switch (effective) {
    case 'off':
      return false;
    case 'always':
      return true;
    case 'incoming':
      // 상대방 메시지만 번역 — 내 말은 원문 유지 (대화 흐름 유지)
      return !isOwnMessage;
    case 'notification':
      // 목록/알림에서만, 본문은 원문
      return slot !== 'body';
  }
}

/** 번역 대상 언어: 방별 오버라이드 > 전역 지정 > 자동 감지 */
export function resolveTargetLang(
  globalLang: Locale,
  autoDetect: boolean,
  roomOverride?: Locale,
): Locale | 'auto' {
  return roomOverride ?? (autoDetect ? 'auto' : globalLang);
}

/** 번역 대상 언어와 원문 언어가 같으면 번역 불필요 (API 호출 절약) */
export function needsTranslation(source: Locale, target: Locale): boolean {
  return source !== target;
}

// ── 검색 토큰 (PLAN 5-4-1장) ───────────────────────────────────────────

const CJK_RANGES = /[぀-ヿ㐀-䶿一-鿿豈-﫿가-힯]/;

/** CJK(한/일/중)인지 판별 — 형태소 경계가 없으므로 n-gram이 필요 */
export function containsCjk(text: string): boolean {
  return CJK_RANGES.test(text);
}

/** 라틴계 단어 분리 */
const LATIN_STOPWORDS = new Set([
  'the',
  'a',
  'an',
  'is',
  'are',
  'was',
  'were',
  'to',
  'of',
  'and',
  'or',
  'in',
  'on',
  'el',
  'la',
  'los',
  'las',
  'un',
  'una',
  'es',
  'de',
  'que',
  'y',
  'la',
  'của',
  'và',
  'là',
  'các',
  'có',
]);

/**
 * 검색 인덱스용 토큰 생성.
 * - CJK: 2-gram 슬라이딩 (예: "안녕하세요" → 안녕/녕하/하세/세요)
 * - 라틴계: 소문자 단어, 2자 이상, 불용어 제외
 * - 숫자: 그대로
 *
 * Firestore `array-contains`는 배열 완전 일치를 요구하므로,
 * 검색어도 동일한 방식으로 토큰화해야 조회된다.
 */
export function tokenizeForSearch(text: string, maxTokens = 24): string[] {
  const tokens = new Set<string>();
  const lower = text.toLowerCase();

  if (containsCjk(lower)) {
    // CJK 구간만 추출해 2-gram
    const cjkOnly = lower.replace(
      /[^\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu,
      '',
    );
    for (let i = 0; i < cjkOnly.length - 1; i += 1) {
      tokens.add(cjkOnly.slice(i, i + 2));
      if (tokens.size >= maxTokens) break;
    }
  }

  for (const word of lower.split(/[^\p{L}\p{N}]+/u)) {
    if (word.length < 2) continue;
    if (LATIN_STOPWORDS.has(word)) continue;
    if (containsCjk(word)) continue; // 위에서 처리
    tokens.add(word);
    if (tokens.size >= maxTokens) break;
  }

  return [...tokens];
}

/** 검색어를 메시지와 동일한 규칙으로 토큰화 */
export function tokenizeQuery(query: string): string[] {
  return tokenizeForSearch(query, 8);
}

/**
 * 서버 쿼리에 넘길 대표 토큰 1개를 고른다.
 *
 * Firestore는 **같은 필드에 array-contains를 두 번 걸 수 없다**(AND 불가).
 * 그래서 가장 선택적인(=긴) 토큰 하나로 서버에서 후보를 좁히고,
 * 나머지 토큰은 클라이언트에서 AND 판정한다 (PLAN 5-4-1장 최대 3단어 AND).
 */
export function primarySearchToken(queryTokens: readonly string[]): string | null {
  let best: string | null = null;
  for (const token of queryTokens) {
    if (best === null || token.length > best.length) best = token;
  }
  return best;
}

/**
 * 메시지 토큰이 검색어 토큰을 **모두** 포함하는지 (AND).
 * 서버는 대표 토큰만 보장하므로 나머지는 여기서 최종 판정한다.
 */
export function matchesQueryTokens(
  messageTokens: readonly string[] | undefined,
  queryTokens: readonly string[],
): boolean {
  if (queryTokens.length === 0) return false;
  if (!messageTokens || messageTokens.length === 0) return false;
  const set = new Set(messageTokens);
  return queryTokens.every((token) => set.has(token));
}
